import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from './Header';
import { formatDate, formatTime, formatTimeRange } from './utils/helpers';
import './scss/Match.scss';
import Attendees from './components/Attendees';
import { FaPencilAlt, FaTrash, FaMapMarkerAlt, FaCopy } from 'react-icons/fa';
import api from './services/apiService';
import { authService } from './services/authService';
import { useNotification } from './components/NotificationProvider';
import GoogleMap from './utils/GoogleMap';
import EditTrainingModal from './components/training/EditTrainingModal';
import TrainingNotes from './components/training/TrainingNotes';

function Training() {
  const { trainingId, tab } = useParams();
  const [trainingDate, setTrainingDate] = useState('');
  const [location, setLocation] = useState('');
  const [trainingTime, setTrainingTime] = useState('');
  const [response, setResponse] = useState(null);
  const [inviteId, setInviteId] = useState(null);
  const playerId = sessionStorage.getItem('player_id');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(tab || 'details');
  const [attendance, setAttendance] = useState({ yes: [], no: [], awaiting: [] });
  const [isAdmin, setIsAdmin] = useState(false);
  const [maxPlayers, setMaxPlayers] = useState(null);
  const [totalYes, setTotalYes] = useState(0);
  const [trainingDetails, setTrainingDetails] = useState(null);
  const [players, setPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pitchDetails, setPitchDetails] = useState(null);
  const { showSuccess, showError } = useNotification();

  // Helper function to open address in maps
  const openInMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    
    if (isIOS) {
      // On iOS, try Apple Maps first (will open app if installed, otherwise web)
      // If user wants Google Maps, they can choose it from the options
      window.open(`https://maps.apple.com/?q=${encodedAddress}`, '_blank');
    } else {
      // On Android and desktop, use Google Maps
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  };

  // Helper function to copy address to clipboard with iOS Safari fallback
  const copyAddressToClipboard = async (address) => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(address);
        showSuccess('Address copied to clipboard!');
        return;
      }
      
      // Fallback for iOS Safari and older browsers
      const textArea = document.createElement('textarea');
      textArea.value = address;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showSuccess('Address copied to clipboard!');
        } else {
          throw new Error('execCommand failed');
        }
      } catch (err) {
        throw new Error('Copy command failed');
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy address:', err);
      showError('Failed to copy address. Please try again.');
    }
  };

  useEffect(() => {
    // Check if the user is an admin
    const checkAdmin = () => {
      const admin = authService.isAdmin() || authService.isManager();
      setIsAdmin(admin);
    };
    
    checkAdmin();
    
    const fetchTrainingDetails = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        const isSuperAdminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
        
        // Build the API URL
        let apiUrl = process.env.REACT_APP_API_URL + `/get_invite.php?match_id=${trainingId}&team_id=${teamId}&type=training&ignore_date_filter=1`;
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        // If a superadmin is viewing as a player, add the player_id as a special parameter
        if (isSuperAdminViewing && playerId) {
          apiUrl += `&superadmin_view_as_player_id=${playerId}`;
        }
        
        console.log('Fetching training details with URL:', apiUrl); // Debug log
        
        const response = await api.get(apiUrl);
        const result = response.data;
        
        console.log('Training API response:', result); // Debug log
        
        if (result.success && result.matches.length > 0) {
          const trainingData = result.matches[0]; // Get the first (and only) training
          const trainingDataWithId = {
            ...trainingData,
            match_id: trainingId  // Add match_id from URL params
          };
          
          setTrainingDate(formatDate(trainingData.match_date));
          setLocation(trainingData.location);
          setTrainingTime(formatTime(trainingData.match_time));
          setResponse(trainingData);
          setInviteId(trainingData.invite_id);
          setMaxPlayers(trainingData.max_players);
          setTotalYes(trainingData.total_yes);
          setTrainingDetails(trainingDataWithId);
          
          // Process pitch details from the response
          if (trainingData.pitch_id && trainingData.pitch_name) {
            const pitchData = {
              pitch_id: trainingData.pitch_id,
              name: trainingData.pitch_name,
              address_1: trainingData.pitch_address_1 || '',
              address_2: trainingData.pitch_address_2 || '',
              town: trainingData.pitch_town || '',
              county: trainingData.pitch_county || '',
              postcode: trainingData.pitch_postcode || '',
              facilities: trainingData.pitch_facilities || '',
              organiser_contact: trainingData.pitch_contact || ''
            };
            
            setPitchDetails(pitchData);
          } else if (trainingData.location) {
            // Create a fallback pitch details object with just the location
            setPitchDetails({
              name: trainingData.location,
              address_1: trainingData.location,
              town: '',
              county: '',
              postcode: '',
              // No facilities or organiser_contact in fallback
            });
          }
        } else {
          console.error('Failed to fetch training details:', {
            success: result.success,
            matchesLength: result.matches?.length,
            result
          });
        }
      } catch (error) {
        console.error('Error fetching training details:', error);
      }
    };

    const fetchAttendance = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        const isSuperAdminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_attendance.php?match_id=${trainingId}&team_id=${teamId}`;
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        // If a superadmin is viewing as a player, add the player_id as a special parameter
        if (isSuperAdminViewing && playerId) {
          apiUrl += `&superadmin_view_as_player_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const data = response.data;
        
        if (data.success) {
          // Set all players with their responses
          setPlayers([
            ...(data.data.yes || []),
            ...(data.data.no || []),
            ...(data.data.awaiting || [])
          ]);

          // Store the full attendance data including uninvited
          setAttendance(data.data);

          // If you need a flat array of all players for other purposes
          setAllPlayers([
            ...(data.data.yes || []),
            ...(data.data.no || []),
            ...(data.data.awaiting || [])
          ]);
        }
      } catch (error) {
        console.error('Error fetching attendance:', error);
      }
    };

    fetchTrainingDetails();
    if (trainingId) {
      fetchAttendance();
    }
  }, [playerId, trainingId]);

  const handleResponseChange = async (newResponse) => {
    // Add debug logging
    console.log('Updating response:', {
      inviteId,
      newResponse,
      trainingId,
      playerId
    });

    try {
      let result;
      
      // Check if we need to create a new invite or update an existing one
      if (!inviteId) {
        console.log('No existing invite found, creating new invite');
        
        // Create a new invite
        const response = await api.post(process.env.REACT_APP_API_URL + `/create_invite.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: `match_id=${trainingId}&player_id=${playerId}&response=${newResponse}&type=training`,
        });
        
        result = response.data;
        console.log('Create invite result:', result);
      } else {
        // Update an existing invite
        const response = await api.post(process.env.REACT_APP_API_URL + `/update_invite.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: `invite_id=${inviteId}&response=${newResponse}`,
        });
        
        result = response.data;
        console.log('Update invite result:', result);
      }

      if (result.success) {
        setResponse(newResponse);
        showSuccess(`Response updated to "${newResponse}" successfully`, 5000);
        
        // Refresh training details and attendance data after updating response
        const teamId = sessionStorage.getItem('selected_team_id');
        
        // Refresh the training details by fetching again
        // Use async/await to refetch the training data
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        const isSuperAdminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
        
        // Build the API URL
        let apiUrl = process.env.REACT_APP_API_URL + `/get_invite.php?match_id=${trainingId}&team_id=${teamId}&type=training&ignore_date_filter=1`;
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        // If a superadmin is viewing as a player, add the player_id as a special parameter
        if (isSuperAdminViewing && playerId) {
          apiUrl += `&superadmin_view_as_player_id=${playerId}`;
        }
        
        console.log('Refreshing training details with URL:', apiUrl); // Debug log
        
        const trainingResponse = await api.get(apiUrl);
        const trainingResult = trainingResponse.data;
        
        console.log('Training refresh response:', trainingResult); // Debug log
        
        if (trainingResult.success && trainingResult.matches.length > 0) {
          const trainingData = trainingResult.matches[0];
          const trainingDataWithId = {
            ...trainingData,
            match_id: trainingId
          };
          
          setTrainingDate(formatDate(trainingData.match_date));
          setLocation(trainingData.location);
          setTrainingTime(formatTime(trainingData.match_time));
          setResponse(trainingData);
          setInviteId(trainingData.invite_id);
          setMaxPlayers(trainingData.max_players);
          setTotalYes(trainingData.total_yes);
          setTrainingDetails(trainingDataWithId);
          
          // Process pitch details from the response
          if (trainingData.pitch_id && trainingData.pitch_name) {
            const pitchData = {
              pitch_id: trainingData.pitch_id,
              name: trainingData.pitch_name,
              address_1: trainingData.pitch_address_1 || '',
              address_2: trainingData.pitch_address_2 || '',
              town: trainingData.pitch_town || '',
              county: trainingData.pitch_county || '',
              postcode: trainingData.pitch_postcode || '',
              facilities: trainingData.pitch_facilities || '',
              organiser_contact: trainingData.pitch_contact || ''
            };
            
            setPitchDetails(pitchData);
          } else if (trainingData.location) {
            // Create a fallback pitch details object with just the location
            setPitchDetails({
              name: trainingData.location,
              address_1: trainingData.location,
              town: '',
              county: '',
              postcode: '',
              // No facilities or organiser_contact in fallback
            });
          }
        }
        
        // Refresh attendance data
        const attendanceResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_attendance.php?match_id=${trainingId}&team_id=${teamId}`);
        if (attendanceResponse.data.success) {
          setAttendance(attendanceResponse.data.data);
          setPlayers([
            ...(attendanceResponse.data.data.yes || []),
            ...(attendanceResponse.data.data.no || []),
            ...(attendanceResponse.data.data.awaiting || [])
          ]);
        }
      } else {
        console.error('Failed to update response:', result);  // Debug log
        showError('Failed to update response. Please try again.', 5000);
      }
    } catch (error) {
      console.error('Error updating response:', error);
      showError('An error occurred while updating your response', 5000);
    }
  };

  const isTrainingStarted = () => {
    if (!trainingDetails) {
        return false;
    }
    
    if (!trainingDetails.match_date || !trainingDetails.match_time) {
        return false;
    }
    
    try {
        const trainingDateTime = new Date(
            `${trainingDetails.match_date} ${trainingDetails.match_time}`
        );
        
        // Check if the date is valid
        if (isNaN(trainingDateTime.getTime())) {
            console.error("isTrainingStarted: invalid date object created", {
                match_date: trainingDetails.match_date,
                match_time: trainingDetails.match_time,
                trainingDateTime: trainingDateTime
            });
            return false;
        }
        
        return new Date() > trainingDateTime;
    } catch (error) {
        console.error("isTrainingStarted error:", error, {
            match_date: trainingDetails.match_date,
            match_time: trainingDetails.match_time
        });
        return false;
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    navigate(`/training/${trainingId}/${newTab}`);
  };

  const handleDeleteTraining = async () => {
    // Add confirmation dialog
    const isConfirmed = window.confirm(
      'Are you sure you want to delete this training session? This action cannot be undone.'
    );
    
    if (!isConfirmed) {
      return; // User cancelled the operation
    }
    
    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/delete_match.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          match_id: trainingId
        })
      });
      
      if (response.data.success) {
        showSuccess('Training session deleted successfully', 5000);
        navigate('/dashboard');
      } else {
        console.error('Failed to delete training session:', response.data.message);
        showError('Failed to delete training session: ' + (response.data.message || 'Unknown error'), 5000);
      }
    } catch (error) {
      console.error('Error deleting training session:', error);
      showError('An error occurred while deleting the training session', 5000);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <div className="match-details">
            <div className="match-header">
              <h3 className="tab-title">Training Details</h3>
            </div>

            {/* Show status message only for cancelled or postponed training sessions */}
            {trainingDetails?.status && (trainingDetails.status === 'cancelled' || trainingDetails.status === 'postponed') && (
              <div className="match-status-message">
                <div className={`status-banner ${trainingDetails.status}`}>
                  {trainingDetails.status === 'cancelled' ? 'Cancelled' : 
                   trainingDetails.status === 'postponed' ? 'Postponed' : ''}
                </div>
                {trainingDetails.last_update_message && (
                  <p className="status-reason">
                    {trainingDetails.last_update_message}
                  </p>
                )}
              </div>
            )}

            <p>Training Date:<br/> <span className="audiowide">{trainingDate}</span></p>

            {/* Only show response buttons if training is active and user is not admin */}
            {(!trainingDetails?.status || trainingDetails.status === 'active') && !isAdmin && (
              <>
                {/* Response Buttons */}
                {response === null ? (
                    <div className='response-section'>
                        <p>{authService.isParent() ? `Can ${sessionStorage.getItem('child_name')?.split(' ')[0] || 'your child'} attend training?` : 'Can you attend training?'}</p>
                        <div className='response-buttons'>
                            <button 
                                className={`response-btn yes ${authService.isChild() ? 'disabled' : ''}`} 
                                onClick={() => !authService.isChild() && handleResponseChange('Yes')}
                                disabled={authService.isChild()}>Yes</button>
                            <button 
                                className={`response-btn no ${authService.isChild() ? 'disabled' : ''}`} 
                                onClick={() => !authService.isChild() && handleResponseChange('No')}
                                disabled={authService.isChild()}>No</button>
                        </div>
                    </div>
                ) : ( '' )}            
              </>
            )}

            <p>Training Time:<br/> 
                <span className="audiowide">
                    {formatTimeRange(trainingDetails?.match_time, trainingDetails?.duration)}
                </span>
            </p>
            <p>Location:<br/> <span className="audiowide">{pitchDetails?.name || location}</span></p>

            {/* Only show response section if training is active */}
            {(!trainingDetails?.status || trainingDetails.status === 'active') && (
              <div>
                {response !== null && !isAdmin ? (
                    <div className="response-section">
                        <p>{authService.isParent() ? `Can ${sessionStorage.getItem('child_name')?.split(' ')[0] || 'your child'} attend training?` : 'Can you attend training?'}</p>
                        <div className="response-buttons">
                            <button 
                                className={`response-btn yes ${response?.response === 'Yes' ? 'active' : response?.response === 'No' ? 'inactive' : ''} ${authService.isChild() ? 'disabled' : ''}`}
                                onClick={() => !authService.isChild() && handleResponseChange('Yes')}
                                disabled={response?.response === 'Yes' || authService.isChild()}
                            >
                                Yes
                            </button>
                            <button 
                                className={`response-btn no ${response?.response === 'No' ? 'active' : response?.response === 'Yes' ? 'inactive' : ''} ${authService.isChild() ? 'disabled' : ''}`}
                                onClick={() => !authService.isChild() && handleResponseChange('No')}
                                disabled={response?.response === 'No' || authService.isChild()}
                            >
                                No
                            </button>
                        </div>
                        
                        {/* Show responder information */}
                        <div className="responder-info">
                            <small style={{ fontStyle: 'italic', color: '#666' }}>
                                {response?.response && response?.responder_first_name && response?.responder_last_name ? (
                                    <>
                                        Responded by {response.responder_first_name.charAt(0).toUpperCase() + response.responder_first_name.slice(1)} {response.responder_last_name.charAt(0).toUpperCase() + response.responder_last_name.slice(1)}
                                        {response?.response_updated ? (
                                            <> on {new Date(response.response_updated).toLocaleDateString()} at {new Date(response.response_updated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                        ) : (
                                            <> on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                        )}
                                    </>
                                ) : (
                                    <>Awaiting response</>
                                )}
                            </small>
                        </div>
                    </div>
                ) : ( '' )}
              </div>
            )}
            

          </div>
        );
      case 'location':
        // Build the full address string
        const trainingFullAddress = pitchDetails 
          ? `${pitchDetails.address_1}${pitchDetails.address_2 ? ', ' + pitchDetails.address_2 : ''}, ${pitchDetails.town}${pitchDetails.county ? ', ' + pitchDetails.county : ''}${pitchDetails.postcode ? ', ' + pitchDetails.postcode : ''}`
          : location;

        return (
          <div className="match-location">
            <div className="match-header">
              <h3 className="tab-title">Location</h3>
            </div>
            
            {/* Action buttons */}
            {(location || pitchDetails) && trainingFullAddress && (
              <div className="location-action-buttons">
                <button 
                  className="location-action-btn open-maps-btn"
                  onClick={() => openInMaps(trainingFullAddress)}
                  aria-label="Open in maps"
                >
                  <FaMapMarkerAlt /> Open in Maps
                </button>
                <button 
                  className="location-action-btn copy-address-btn"
                  onClick={() => copyAddressToClipboard(trainingFullAddress)}
                  aria-label="Copy address"
                >
                  <FaCopy /> Copy Address
                </button>
              </div>
            )}
            
            {/* Add Google Map for the training location */}
            {(location || pitchDetails) && (
              <div className="match-location-map">
                <GoogleMap 
                  addresses={[
                    pitchDetails ? 
                    `${pitchDetails.address_1}, ${pitchDetails.address_2 || ''}, ${pitchDetails.town}, ${pitchDetails.county || ''}, ${pitchDetails.postcode}` 
                    : location
                  ]} 
                />
              </div>
            )}
            
            {/* Full address details */}
            {pitchDetails && (
              <div className="address-details">
                <h4>Full Address:</h4>
                <p className="address-text">
                  {pitchDetails.name && <strong>{pitchDetails.name}</strong>}
                  {pitchDetails.address_1 && <br />}{pitchDetails.address_1}
                  {pitchDetails.address_2 && <br />}{pitchDetails.address_2}
                  {pitchDetails.town && <br />}{pitchDetails.town}
                  {pitchDetails.county && <br />}{pitchDetails.county}
                  {pitchDetails.postcode && <br />}{pitchDetails.postcode}
                </p>
                {pitchDetails.facilities && (
                  <div className="facilities">
                    <h4>Facilities:</h4>
                    <p>{pitchDetails.facilities}</p>
                  </div>
                )}
                {pitchDetails.organiser_contact && (
                  <div className="contact">
                    <h4>Contact:</h4>
                    <p>{pitchDetails.organiser_contact}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Fallback for location-only data */}
            {!pitchDetails && location && (
              <div className="address-details">
                <h4>Location:</h4>
                <p className="address-text">{location}</p>
              </div>
            )}
          </div>
        );
      case 'attendance':
        return (
          <Attendees 
            attendance={{
              yes: players.filter(p => p.response === 'Yes'),
              no: players.filter(p => p.response === 'No'),
              awaiting: players.filter(p => !p.response || p.response === 'Awaiting'),
              uninvited: attendance.uninvited
            }}
            isAdmin={isAdmin}
            match_id={trainingId}
            maxPlayers={maxPlayers}
            type="training"
          />
        );
      case 'notes':
        return <TrainingNotes />;
      default:
        return null;
    }
  };

  return (
    <div className="Match container">
      <Header />
      <div className="header-actions">
        <button className="back-button" onClick={() => window.history.back()}>← Back</button>
        {isAdmin && (
          <>
            <button 
              className="edit-match-button"
              onClick={() => setShowEditModal(true)}
              title="Edit Training"
            >
              <FaPencilAlt />
            </button>
            <button 
              className="delete-match-button"
              onClick={handleDeleteTraining}
              title="Delete Training"
            >
              <FaTrash />
            </button>
          </>
        )}
      </div>
      
      {/* Show tabs if training is active, or if there are time/venue changes */}
      {(!trainingDetails?.status || trainingDetails.status === 'active' || trainingDetails.status === 'time_changed' || trainingDetails.status === 'venue_changed' || trainingDetails.status === 'venue_time_changed') && (
        <div className="tabs">
          <button 
            className={activeTab === 'details' ? 'active' : ''} 
            onClick={() => handleTabChange('details')}
          >
            Details
          </button>
          <button 
            className={activeTab === 'location' ? 'active' : ''} 
            onClick={() => handleTabChange('location')}
          >
            Location
          </button>
          <button 
            className={activeTab === 'attendance' ? 'active' : ''} 
            onClick={() => handleTabChange('attendance')}
          >
            Attendees
          </button>
          {/* Notes tab - only visible to coaches */}
          {(authService.isManager() || sessionStorage.getItem('user_type') === 'coach') && (
            <button 
              className={activeTab === 'notes' ? 'active' : ''} 
              onClick={() => handleTabChange('notes')}
            >
              Notes
            </button>
          )}
        </div>
      )}
      
      <div className="tab-content">
        {renderTabContent()}
      </div>

      {showEditModal && (
        <EditTrainingModal
          match={{
            match_id: trainingId,
            ...trainingDetails,
            type: 'training'
          }}
          onMatchUpdated={(updatedTraining) => {
            // Update all the state variables to reflect the changes
            setTrainingDetails(updatedTraining);
            setTrainingDate(formatDate(updatedTraining.match_date));
            setTrainingTime(formatTime(updatedTraining.match_time));
            setLocation(updatedTraining.location);
            setMaxPlayers(updatedTraining.max_players);
            setTotalYes(updatedTraining.total_yes || totalYes);
            
            // Create or update pitch details if available
            if (updatedTraining.pitch_id && updatedTraining.pitch_name) {
              const pitchData = {
                pitch_id: updatedTraining.pitch_id,
                name: updatedTraining.pitch_name,
                address_1: updatedTraining.pitch_address_1 || '',
                address_2: updatedTraining.pitch_address_2 || '',
                town: updatedTraining.pitch_town || '',
                county: updatedTraining.pitch_county || '',
                postcode: updatedTraining.pitch_postcode || '',
                facilities: updatedTraining.pitch_facilities || '',
                organiser_contact: updatedTraining.pitch_contact || ''
              };
              
              setPitchDetails(pitchData);
            } else if (updatedTraining.location) {
              // Create a fallback pitch details object with just the location
              setPitchDetails({
                name: updatedTraining.location,
                address_1: updatedTraining.location,
                town: '',
                county: '',
                postcode: '',
                // No facilities or organiser_contact in fallback
              });
            }
            
            // Show success notification to the user
            showSuccess('Training session updated successfully', 5000);
            
            // Close the modal
            setShowEditModal(false);
          }}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  );
}

export default Training; 